export interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings?: string[]
}

/**
 * Validates a hideIf expression string, supporting:
 * 1. Basic comparison: property operator value (e.g., age > 18)
 * 2. Logical grouping: (condition && condition) || condition
 */
export function validateHideIfExpression(conditionStr: string): ValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  // Check if expression is empty or not a string
  if (!conditionStr || typeof conditionStr !== 'string') {
    return { isValid: false, errors: ['Expression cannot be empty'] }
  }

  const trimmedCondition = conditionStr.trim()
  if (!trimmedCondition) {
    return { isValid: false, errors: ['Expression cannot be empty'] }
  }

  try {
    // --- 1. Define Single Condition Pattern (C) ---
    // C: property operator value (e.g., user_role == "Admin")
    const conditionC = '([a-zA-Z0-9_-]+(?:\\.[a-zA-Z0-9_-]+)*\\s*(==|!=|>=|<=|>|<)\\s*(".*?"|\'.*?\'|null|undefined|true|false|\\d{4}-\\d{2}-\\d{2}|[a-zA-Z0-9_-]+))';

    // Regex pattern to match a single "Condition" (C)
    const singleConditionPattern = new RegExp(conditionC, 'g');

    // --- 2. Check Parentheses Balance ---
    let balance = 0
    for (const char of trimmedCondition) {
      if (char === '(') {
        balance++
      } else if (char === ')') {
        balance--
      }
      if (balance < 0) {
        errors.push('Mismatched closing parenthesis ")" or misplaced parenthesis.')
        break
      }
    }

    if (balance !== 0) {
      errors.push('Mismatched opening parenthesis "(".')
    }

    // --- 3. Simplification and Logical Structure Check ---
    if (errors.length === 0) {
      // Replace all single conditions (C) with the placeholder 'C'
      let simplifiedExpression = trimmedCondition.replace(singleConditionPattern, 'C');

      // Remove spaces
      simplifiedExpression = simplifiedExpression.replace(/\s+/g, '');

      // Check for remaining text outside C, (, ), &&, ||
      const invalidCharsMatch = simplifiedExpression.match(/[^(C&|)]/g);
      if (invalidCharsMatch) {
        errors.push(`Invalid expression syntax. Found unrecognized characters or structure (ensure every single condition is validated)`);
      }

      // Check for double logical operators or wrong operator sequence (e.g., C && && C, or C && || C)
      if (simplifiedExpression.includes('&&&&') || simplifiedExpression.includes('||||') ||
        simplifiedExpression.includes('&&||') || simplifiedExpression.includes('||&&')) {
        errors.push('Invalid double logical operator. Use && or ||.');
      }

      // Remove parentheses for the operator-condition sequence check
      const logicalCheckString = simplifiedExpression.replace(/(\(|\))/g, '');

      // Check if string starts or ends with a logical operator
      if (logicalCheckString.startsWith('&&') || logicalCheckString.startsWith('||') ||
        logicalCheckString.endsWith('&&') || logicalCheckString.endsWith('||')) {
        errors.push('Logical expression cannot start or end with a logical operator (&& or ||).');
      }
    }


    // --- 4. Check Comparison and Logical Operators ---

    // Check for comparison operators
    const operatorRegex = /(==|!=|>=|<=|>|<)/g
    const operators = trimmedCondition.match(operatorRegex)
    if (!operators) {
      errors.push('Missing comparison operator. Use: ==, !=, >, <, >=, <=')
    }

    // Check for invalid single operators (=, &, |)
    const invalidOperators = trimmedCondition.match(/(?<![=!><])[=](?![=])|(?<![&])[&](?![&])|(?<![|])[|](?![|])/g)
    if (invalidOperators) {
      errors.push('Invalid operator found. Use == instead of =, && instead of &, || instead of |')
    }

    // Check for === and !==
    if (trimmedCondition.includes('===')) {
      errors.push('Use == instead of === for comparisons')
    }
    if (trimmedCondition.includes('!==')) {
      errors.push('Use != instead of !== for comparisons')
    }

    // --- 5. Check Quotes ---

    // Find all string literals
    const stringLiterals = Array.from(trimmedCondition.matchAll(/"([^"\\]*(?:\\.[^"\\]*)*)"|'([^'\\]*(?:\\.[^'\\]*)*)'/g)).map(m => m[0]);

    // Count total quotes
    const singleQuotes = (trimmedCondition.match(/'/g) || []).length
    const doubleQuotes = (trimmedCondition.match(/"/g) || []).length

    if (singleQuotes % 2 !== 0) {
      errors.push('Unmatched single quotes in expression')
    }
    if (doubleQuotes % 2 !== 0) {
      errors.push('Unmatched double quotes in expression')
    }

    // Check for unescaped quotes in string values
    stringLiterals.forEach((stringValue) => {
      if (stringValue.includes('\n') || stringValue.includes('\r')) {
        errors.push(`String value contains line breaks: ${stringValue}`)
      }
    })

    // Add information about array handling (reinstated for ==/!=)
    const equalsOperators = trimmedCondition.match(/(==|!=)/g)
    if (equalsOperators && equalsOperators.length > 0) {
      warnings.push(
        'Note: For properties with array values, == checks if the array includes the value, != checks if the array does not include the value'
      )
    }


    // --- 6. Check Property Names ---

    // Properties only from C (property operator value)
    const propertyMatches = trimmedCondition.matchAll(/([a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9_-]+)*)\s*(==|!=|>=|<=|>|<)/g)

    const properties = Array.from(propertyMatches).map((match) => match[1])

    // Check if there are properties where expected
    if (properties.length === 0 && trimmedCondition.match(operatorRegex)) {
      errors.push('No valid property names found in expression')
    } else {
      properties.forEach((prop) => {
        // Check property name format
        if (!/^[a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9_-]+)*$/.test(prop)) {
          errors.push(`Invalid property name: "${prop}". Use only letters, numbers, underscores, and hyphens`)
        }

        // Check if property starts with number
        if (/^[0-9]/.test(prop)) {
          errors.push(`Property name "${prop}" cannot start with a number`)
        }

        // Warn about property names with hyphens
        if (prop.includes('-')) {
          warnings.push(`Property "${prop}" contains hyphens. Ensure this matches your data structure`)
        }
      })
    }

    // --- 7. Check Date Format ---

    const dateRegex = /\d{4}-\d{2}-\d{2}/g
    const dates = trimmedCondition.match(dateRegex)
    if (dates) {
      dates.forEach((date) => {
        const [year, month, day] = date.split('-').map(Number)

        if (year < 1900 || year > 2100) {
          errors.push(`Invalid year in date: ${date}. Year should be between 1900 and 2100`)
        }
        if (month < 1 || month > 12) {
          errors.push(`Invalid month in date: ${date}. Month should be between 01 and 12`)
        }
        if (day < 1 || day > 31) {
          errors.push(`Invalid day in date: ${date}. Day should be between 01 and 31`)
        }

        // Additional date validation (check for non-existent date like Feb 30th)
        const dateObj = new Date(year, month - 1, day)
        if (dateObj.getFullYear() !== year || dateObj.getMonth() !== month - 1 || dateObj.getDate() !== day) {
          errors.push(`Invalid date: ${date}. This date does not exist`)
        }
      })
    }

  } catch (error) {
    errors.push(`General validation error: ${(error as Error).message}`)
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: warnings.length > 0 ? warnings : undefined,
  }
}

// Helper function to get user-friendly error messages
export function getValidationMessage(result: ValidationResult): string {
  if (result.isValid) {
    return 'Expression is valid'
  }

  return result.errors.join('; ')
}

// Helper function to get examples of valid expressions
export function getExpressionExamples(): string[] {
  return [
    'user_role == "Admin"',
    'status != "inactive"',
    'age >= 18',
    'created_date > 2025-01-01',
    'input-field == "value"',
    'is_active == true',
    'data_value == null',
    'user_role == "Admin" && status == "active"',
    '(age >= 18 || user_role == "Manager") && status != "pending"', // With parentheses
    'selected_options == "option1"', // Array inclusion check
    'tags != "important"', // Array exclusion check
  ]
}